import { Helmet } from 'react-helmet-async';
import PageLayout from '../../components/PageLayout';
import { Phone, MapPin, Building2, CheckCircle2 } from 'lucide-react';

const KannurLanding = () => {
  return (
    <>
      <Helmet>
        <title>Custom Home Builders in Kannur | Master Home</title>
        <meta name="description" content="Build your dream home in Kannur with Master Home. Trusted local builders for custom homes, villas & turnkey construction." />
        <link rel="canonical" href="https://masterhome.co.in/locations/kannur" />
      </Helmet>

      <PageLayout
        title="Custom Home Builders in Kannur, Kerala"
        subtitle="Bringing your dream home to life with premium construction services in the heart of Kannur."
      >
        <div className="space-y-16">
          {/* Introduction */}
          <section className="glass-card p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6 text-gradient">Your Trusted Local Builders in Kannur</h2>
            <div className="prose max-w-none text-gray-600 text-lg leading-relaxed">
              <p className="mb-4">
                Are you looking to build the perfect family home or a luxury villa in Kannur? For growing families, NRIs returning home, and local professionals, finding a trustworthy contractor is the most critical step. At Master Home, we understand the common pain points of building in Kerala—budget overruns, unexpected delays, and compromised quality.
              </p>
              <p>
                As dedicated home builders in Kannur, we eliminate these anxieties through transparent pricing, strict project timelines, and uncompromising material quality. From the foundation to the final coat of paint, our turnkey construction services are designed to give you total peace of mind.
              </p>
            </div>
          </section>

          {/* Local Proof */}
          <section className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6 lg:pr-8">
              <h2 className="text-3xl font-bold text-gray-900">Building for Kannur's Unique Climate</h2>
              <p className="text-gray-600 text-lg">
                Kannur's heavy monsoons and humid summers demand specialized building techniques.
                Whether you're situated near the Payyambalam beach requiring corrosion-resistant materials,
                or in the undulating terrains near Thottada, our architectural designs are tailored to the local geography.
              </p>
              <p className="text-gray-600 text-lg">
                We have successfully delivered stunning residential projects across premium neighborhoods in Kannur, ensuring that every home is naturally ventilated, energy-efficient, and Vastu-compliant.
              </p>
            </div>
            <div className="bg-primary-50 p-8 rounded-2xl border border-primary-100">
              <h3 className="text-xl font-bold text-primary-800 mb-4 flex items-center gap-2">
                <MapPin className="text-primary-600" /> Key Areas We Serve
              </h3>
              <ul className="space-y-3">
                {['Thavakkara', 'Thottada', 'Payyambalam', 'Pallikunnu', 'Talap'].map((area) => (
                  <li key={area} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-primary-500" />
                    Residential Construction in {area}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Services */}
          <section className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 p-8">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Construction Services in Kannur</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-bold">Custom Homes & Villas</h3>
                <p className="text-gray-600">End-to-end design and construction of bespoke luxury homes tailored to your lifestyle in Kannur.</p>
              </div>
              <div className="space-y-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-bold">Turnkey Construction</h3>
                <p className="text-gray-600">Hassle-free contracting. We handle everything from approvals and material sourcing to interior finishing.</p>
              </div>
              <div className="space-y-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-bold">Home Renovations</h3>
                <p className="text-gray-600">Modernize your traditional Kerala home with our expert remodeling and structural extension services.</p>
              </div>
            </div>
          </section>

          {/* Trust Section */}
          <section className="text-center max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold">Why Choose Master Home in Kannur?</h2>
            <p className="text-xl text-gray-600">
              With over 10 years of experience shaping the skyline of Kerala, our local expertise is unmatched. We partner with top-tier material brands, employ skilled artisans, and utilize modern project management to ensure 100% quality delivery. Choose the builders who treat your investment like their own.
            </p>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-12 text-center text-white shadow-2xl">
            <h2 className="text-3xl font-bold mb-4">Start Your Construction Project Today</h2>
            <p className="text-xl mb-8 opacity-90">Get a free site visit and consultation with our expert masonry and architectural team.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="tel:+919567810944" className="inline-flex items-center justify-center px-8 py-4 text-primary-700 bg-white rounded-full font-bold text-lg hover:bg-gray-50 transition-colors shadow-lg">
                <Phone className="mr-2" /> Call +91 9567810944
              </a>
              <a href="https://wa.me/919567810944" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-white border-2 border-white rounded-full font-bold text-lg hover:bg-white/10 transition-colors">
                WhatsApp Us
              </a>
            </div>
          </section>
        </div>
      </PageLayout>
    </>
  );
};

export default KannurLanding;
